<template>
    <div>
        <div 
            ref="invoiceContainer" 
            class="max-w-3xl mx-auto px-6 py-10 bg-white rounded-lg shadow my-8 text-left"
        >
            <div v-if="payment" class="text-secondary">
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-start pb-8 border-b border-iron">
                    <div>
                        <img src="@/assets/mp-logo.webp" class="w-1/3 mr-auto pt-2 mb-4">
                        <h1 class="text-2xl font-bold py-1">
                            {{ translate('payment receipt', 'capitalize') }}
                        </h1>
                        <p class="text-sm text-gray-500">
                            {{ DateHandler.format(payment.raw_date, 'DATE_FULL') }}
                        </p>
                    </div>
                    
                    <div class="md:text-right space-y-2 mt-4 md:mt-0">
                        <div class="flex md:justify-end">
                            <span 
                                class="bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-green-200"
                                :class="{ 'print-regulation-area': exporting }"
                            >
                                {{ translate('paid', 'ucfirst') }}
                            </span>
                        </div>
                        <p class="text-sm text-gray-600">
                            <span class="mr-2">{{ translate('transaction number', 'ucfirst') }}:</span>
                            <span class="text-base font-medium text-gray-900" :class="{ 'print-regulation-area': exporting }">
                                {{ payment.payment_id }}
                            </span>
                        </p>
                        <p class="text-sm text-gray-600">
                            <strong class="font-normal text-gray-400">{{ translate('emission', 'ucfirst') }}:</strong> 
                            {{ DateHandler.format(payment.raw_date, 'DATE_SHORT') }}
                        </p>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-8 my-8 text-sm">
                    <div class="border-r border-iron pr-4">
                        <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                            {{ translate('provider', 'ucfirst') }}
                        </h3>
                        <p class="text-lg font-bold text-gray-800 mb-0.5">
                            {{ context.companyNickname }}
                        </p>
                        <p class="text-gray-500 italic text-xs">
                            {{ context.companyName }}
                        </p>
                    </div>
                    <div>
                        <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                            {{ translate('buyer', 'ucfirst') }}
                        </h3>
                        <p class="text-lg font-medium text-gray-800">
                            {{ payment.payer_name || translate('student', 'ucfirst') }}
                        </p>
                    </div>
                </div>

                <div class="my-8">
                    <p class="pt-3 pb-4 text-sm font-semibold border-t border-iron text-gray-500 uppercase tracking-wider">
                        {{ translate('purchase information', 'ucfirst') }}:
                    </p>
                    
                    <div class="space-y-3 pl-2">
                        <div v-for="(item, index) of payment.items" :key="index" class="flex items-center justify-between text-sm py-1">
                            <div class="flex items-center">
                                <i class="pi pi-circle-fill text-[8px] text-gray-400 my-auto"></i>
                                <p class="ml-3 font-medium text-gray-800">{{ item.title }}</p>
                            </div>
                            <p class="font-mono text-gray-600">
                                R$ {{ item.unit_price?.toFixed(2) }}
                            </p>
                        </div>
                    </div>
                </div>

                <div class="mt-8 border-t border-iron pt-5 flex flex-col justify-end text-right">
                    <p class="text-sm text-gray-500 mb-1">{{ translate('total', 'ucfirst') }}:</p>
                    <p class="font-bold text-3xl text-gray-900 font-mono">
                        {{ payment.total }}
                    </p>
                </div>
                
                <div class="mt-12 pt-6 border-t border-iron">
                    <p class="text-xs text-center text-gray-400 leading-relaxed">
                        {{ translate('this is an invoice generated automatically by the system', 'ucfirst') }} {{ context.companyNickname }}.<br>
                        {{ translate('Questions? Contact us.') }} 
                        <a :href="`mailto:${context.supportEmail}`" class="text-blue-600 underline hover:text-blue-800 transition">
                            {{ context.supportEmail }}
                        </a>
                    </p>
                </div>
            </div>
        </div>
        
        <div class="max-w-3xl mx-auto text-right mt-6">
            <Button 
                :label="translate('export to pdf', 'ucfirst')" 
                icon="pi pi-file-pdf" 
                @click="exprotToPdf()" 
                :loading="exporting"
                class="bg-purple-suit-forced hover:opacity-90 text-white px-4 py-2 text-sm rounded shadow-sm transition border-none" 
            />
        </div>

        <LoadingDialog :visible="loading" />
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import DateHandler from "@/composables/handlers/DateHandler";
import { translate } from '@/composables/translate';
import { useRoute } from 'vue-router';
import PaymentService from '@/services/PaymentService';
import urlHandler from '@/composables/urlHandler';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import Button from 'primevue/button';
import html2pdf from 'html2pdf.js';

const { useSessionMessageStore } = require('@/stores/sessionMessage');
const { useContextStore } = require('@/stores/context');

const sessionMessageStore = useSessionMessageStore();
const context = useContextStore();
const route = useRoute();

const payment = ref(null);
const loading = ref(true);
const exporting = ref(false);
const invoiceContainer = ref(null);

onMounted(() => {
    getPayment();
});

async function getPayment() {
    const id = route.params?.id;

    if (!id) {
        redirectToDashboard();
        return;
    }

    loading.value = true;
    const response = await PaymentService.getPayment(id);
    loading.value = false;

    if (!response) {
        redirectToDashboard();
        return;
    }

    payment.value = response.simplifyData();
}

function redirectToDashboard() {
    sessionMessageStore.errorMessage(translate('payment not found'));
    urlHandler.redirectAndScroll('/dashboard');
}

async function exprotToPdf() {
    if (!payment.value) return;

    exporting.value = true;
    await nextTick();
    
    const options = {
        margin: [15, 15, 15, 15], 
        filename: `${translate('receipt', 'ucfirst')}_${payment.value.payment_id}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
            scale: 2, 
            useCORS: true, 
            logging: false 
        },
        jsPDF: { 
            unit: 'mm', 
            format: 'a4', 
            orientation: 'portrait' 
        }
    };

    html2pdf()
        .set(options)
        .from(invoiceContainer.value)
        .save()
        .then(() => {
            exporting.value = false;
        })
        .catch(() => {
            exporting.value = false;
        });
}
</script>

<style scoped>
.print-regulation-area {
    padding-bottom: 15px !important;
}
</style>